// La barra de herramientas de la tabla (`.table-toolbar` del mock): filtros declarados, avisos y
// métricas de lo listado. Va entre la barra de pastillas y la tabla.
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

export const TABLE_TOOLBAR_CLASS =
    'flex flex-none flex-wrap items-center gap-2 border-b border-border px-3 py-1.5 text-[11px] text-muted-foreground';

export interface TableToolbarProps {
    children: ReactNode;
    /** `status` cuando la barra es un aviso que el lector de pantalla debe anunciar. */
    role?: 'status';
    className?: string;
}

export function TableToolbar({ children, role, className }: TableToolbarProps) {
    return (
        <div role={role} className={cn(TABLE_TOOLBAR_CLASS, className)}>
            {children}
        </div>
    );
}

export type ToolbarMetricTone = 'neutral' | 'success' | 'danger' | 'warning' | 'info';

const METRIC_VALUE_TONE: Record<ToolbarMetricTone, string> = {
    neutral: 'text-foreground',
    success: 'text-success',
    danger: 'text-danger',
    warning: 'text-warning',
    info: 'text-info',
};

export interface ToolbarMetricProps {
    label: string;
    value: ReactNode;
    tone?: ToolbarMetricTone;
    /** Sobre qué se calculó el número. */
    title?: string;
}

/** Métrica informativa de la barra (`.stat-metric` del mock): no filtra, solo resume. */
export function ToolbarMetric({ label, value, tone = 'neutral', title }: ToolbarMetricProps) {
    return (
        <span
            title={title}
            className={cn(
                'inline-flex items-baseline gap-1 rounded bg-muted/40 px-1.5 py-0.5 text-[10.5px]',
                title !== undefined && 'cursor-help',
            )}
        >
            <span className="text-[9.5px] font-bold uppercase tracking-[0.04em] text-muted-foreground">
                {label}
            </span>
            <span className={cn('font-bold tabular-nums', METRIC_VALUE_TONE[tone])}>{value}</span>
        </span>
    );
}
